import axios from 'axios';

import { isAllowedAuth0LoginUrl } from '@/utils/redirect';

/**
 * Fetches the BFF user info to detect an existing authenticated session.
 * Resolves with the Axios response on success; rejects (via setupAxios, with
 * error.response) when the session is missing or invalid.
 */
const getUserInfo = () => {
  return axios.get(`${process.env.BFF_USER_API}/info`, {
    withCredentials: true
  });
};

const login = (username: string, password: string, code?: string) => {
  return axios.post(`${process.env.API_INTERNAL_BASEURL}/login`, {
    username,
    password,
    ...(code && { code })
  });
};

const loginByBffUser = (username: string, password: string, code?: string) => {
  const loginUrl = `${process.env.BFF_USER_API}/login/v2`;

  return axios.post(
    loginUrl,
    {
      username,
      password,
      ...(code && { code })
    },
    { withCredentials: true }
  );
};

const forgotPassword = (username: string) => {
  return axios.post(`${process.env.API_INTERNAL_BASEURL}/forgot_password`, {
    username: username
  });
};

const forgotPasswordNewFlow = (email: string) => {
  return axios.post(`${process.env.BFF_USER_API}/users/password-reset`, {
    email
  });
};

const validatePasswordResetToken = (token: string, tokenId: string) => {
  return axios.post(`${process.env.BFF_USER_API}/users/password-reset/validate`, {
    token,
    tokenId
  });
};

const parsePasswordLoginDisabledConnectionIdsFromEnvVar = (
  rawEnvValue: string | undefined
): string[] => {
  if (!rawEnvValue) return [];
  return rawEnvValue
    .split(',')
    .map((item) => item?.trim?.() ?? '')
    .filter((item) => !!item);
};

const checkIfShouldHideContinueWithPassword = (ssoHyperLink: string) => {
  const passwordLoginDisabledConnectionIds =
    parsePasswordLoginDisabledConnectionIdsFromEnvVar(
      process.env.PASSWORD_LOGIN_DISABLED_CONNECTION_IDS
    );

  const decodedUrl = decodeURIComponent(ssoHyperLink);
  const urlObj = new URL(decodedUrl);
  const connectionValue = urlObj.searchParams.get('connection');

  return passwordLoginDisabledConnectionIds.includes(connectionValue);
};

const checkIfUserIsFederated = async (email: string) => {
  const response = await axios.get(
    `${process.env.CW_AUTH_BASE_URL}/federation`,
    { params: { email }, withCredentials: true }
  );

  const {
    is_federated: isFederated = false,
    federation_link: federationLink = ''
  } = response.data?.data || {};

  return {
    ...response,
    data: {
      ...response.data,
      shouldHideContinueWithPassword:
        (isFederated &&
          federationLink &&
          checkIfShouldHideContinueWithPassword(federationLink)) ||
        false
    }
  };
};

/**
 * Fetches the Auth0 Universal Login (ACUL) authorize URL for a user from
 * cw-api-auth. The call also sets the SSO state/verifier/nonce cookies the
 * Auth0 callback needs, hence `withCredentials`.
 *
 * @param email - optional email sent as login_hint. Omit it to start at Auth0's
 *   identifier screen so users can choose passkey sign-in before entering an identifier.
 * @returns the backend-supplied login_url, unchanged.
 * @throws Error('Invalid login_url') when login_url is missing or not a safe
 *   Auth0 /authorize URL; the axios rejection on network/4xx/5xx failures.
 */
const getLoginUrl = async (email?: string): Promise<string> => {
  const { data } = await axios.get(`${process.env.CW_AUTH_BASE_URL}/login-url`, {
    ...(email ? { params: { email } } : {}),
    headers: { 'x-app-name': 'CW' },
    withCredentials: true
  });

  const loginUrl = data?.data?.login_url;
  if (!isAllowedAuth0LoginUrl(loginUrl)) throw new Error('Invalid login_url');

  return loginUrl;
};

export default {
  getUserInfo,
  login,
  loginByBffUser,
  forgotPassword,
  forgotPasswordNewFlow,
  validatePasswordResetToken,
  parsePasswordLoginDisabledConnectionIdsFromEnvVar,
  checkIfShouldHideContinueWithPassword,
  checkIfUserIsFederated,
  getLoginUrl
};
